import { BaseEditor, Element } from 'slate';
import { ReactEditor } from 'slate-react';

// constants
import aampeTheme from '../constants/aampeTheme';
import { FormulaType } from '../constants/formulaType';

import { NotificationButtonElement } from './formula.model';

// models

export type EditorType = BaseEditor & ReactEditor;

export enum SectionType {
    header = 'header',
    body = 'body',
    message = 'message',
    subject = 'subject',
    emailBody = 'email_body',
    headline = 'headline',
    multi_line_input = 'multi_line_input',
    single_line_input = 'single_line_input',
    buttonText = 'button_text',
    buttonLink = 'button_link',
    destination_link_on_image_click = 'destination_link_on_image_click',
    surface_button_text = 'surface_button_text',
    surface_button_action = 'surface_button_action',
}

export enum ElementKind {
    section = 'section',
    variant = 'variant',
    dataset = 'dataset',
    buttonAction = 'button_action',
    buttonText = 'button_text',
    button = 'button',
    image = 'image',
    cms_placeholder = 'cms_placeholder',
    link = 'link',
    surfaceButton = 'surface_button',
    attributePlaceholder = 'attribute_placeholder',
}

export enum ButtonType {
    primary = 'primary',
    secondary = 'secondary',
}

export const primaryButton: ButtonElement = {
    kind: ElementKind.button,
    buttonType: ButtonType.primary,
    children: [
        {
            kind: ElementKind.buttonText,
            children: [{ text: '' }],
        },
        {
            kind: ElementKind.buttonAction,
            children: [{ text: '' }],
        },
    ],
};

export const secondaryButton: ButtonElement = {
    kind: ElementKind.button,
    buttonType: ButtonType.secondary,
    children: [
        {
            kind: ElementKind.buttonText,
            children: [{ text: '' }],
        },
        {
            kind: ElementKind.buttonAction,
            children: [{ text: '' }],
        },
    ],
};

export const getSlateInputType = (element: Element) =>
    element.kind === ElementKind.section ? element.sectionType : element.kind;

export const SlateElementsData: {
    [key in SectionType | ElementKind]?: SlateElementsDataModel;
} = {
    [SectionType.emailBody]: {
        isMultiLine: true,
        label: 'Body',
    },
    [SectionType.body]: {
        isMultiLine: true,
        isRequired: true,
    },
    [SectionType.message]: {
        isMultiLine: true,
    },
    [SectionType.single_line_input]: {
        isMultiLine: false,
    },
    [SectionType.multi_line_input]: {
        isMultiLine: true,
    },
    [ElementKind.image]: {
        label: 'Image URL',
        hideEmoji: true,
        hideCMSPlaceholder: true,
        hideCharCount: true,
        isInlineComponent: false,
        isStaticComponent: true,
        allowVariantCreation: false,
    },
    [ElementKind.button]: {
        isInlineComponent: false,
        isStaticComponent: true,
    },
    [ElementKind.buttonText]: {
        label: 'Text',
        hideCharCount: true,
        isInlineComponent: false,
        isStaticComponent: true,
        allowVariantCreation: true,
    },
    [ElementKind.buttonAction]: {
        label: 'Action',
        hideEmoji: true,
        hideCMSPlaceholder: true,
        hideCharCount: true,
        isInlineComponent: false,
        isStaticComponent: true,
    },
    [SectionType.buttonLink]: {
        label: 'Button link',
        hideEmoji: true,
        hideCMSPlaceholder: true,
        hideCharCount: true,
        isInlineComponent: false,
        isStaticComponent: true,
    },
    [SectionType.destination_link_on_image_click]: {
        label: 'Destination link on image click',
        hideEmoji: true,
        hideCMSPlaceholder: true,
        hideCharCount: true,
        isInlineComponent: false,
        isStaticComponent: true,
        allowVariantCreation: false,
    },
    [ElementKind.section]: {
        isInlineComponent: false,
        isStaticComponent: true,
        allowVariantCreation: true,
    },
    [ElementKind.dataset]: {
        isInlineComponent: true,
        disableLowerMenu: true,
        isVoid: true,
    },
    [ElementKind.cms_placeholder]: {
        isInlineComponent: true,
        disableLowerMenu: true,
        isVoid: true,
    },
    [ElementKind.attributePlaceholder]: {
        isInlineComponent: true,
        disableLowerMenu: true,
        isVoid: true,
    },
    [ElementKind.variant]: {
        isInlineComponent: true,
        disableLowerMenu: true,
        isVoid: true,
    },
    [ElementKind.link]: {
        isInlineComponent: true,
        disableLowerMenu: true,
        isVoid: true,
    },
    [SectionType.subject]: {
        hideLinkCreation: true,
    },
    [SectionType.surface_button_action]: {
        allowVariantCreation: true,
    },
    [SectionType.surface_button_text]: {
        allowVariantCreation: true,
    },
};

interface SlateElementsDataModel {
    isMultiLine?: boolean;
    label?: string;
    hideEmoji?: boolean;
    hideCMSPlaceholder?: boolean;
    hideCharCount?: boolean;
    isInlineComponent?: boolean;
    isStaticComponent?: boolean;
    allowVariantCreation?: boolean;
    disableLowerMenu?: boolean;
    isVoid?: boolean;
    hideLinkCreation?: boolean;
    isRequired?: boolean;
}

export type EditorNodes =
    | SectionElement
    | VariantElement
    | DatasetElement
    | CMSPlaceholderElement
    | AttributePlaceholderElement
    | Text
    | LinkElement
    | SurfaceButtonElement;

export type SectionChild =
    | VariantElement
    | DatasetElement
    | TextElement
    | CMSPlaceholderElement
    | AttributePlaceholderElement
    | LinkElement;

export type ElementChild =
    | VariantElement
    | DatasetElement
    | CMSPlaceholderElement
    | AttributePlaceholderElement;
export interface TextElement {
    text: string;
}

export interface SectionElement {
    kind: ElementKind.section;
    sectionType: SectionType;
    children: SectionChild[];
}

export interface VariantElement {
    kind: ElementKind.variant;
    variantType: string;
    id: string;
    children: TextElement[];
    componentId?: string;
}

export interface ImageBaseElement {
    sectionType: SectionType;
    variantType: string;
    componentId?: string;
    id: string;
    children: TextElement[];
    datasetId?: number;
    datasetField?: string;
    labelId?: number;
}

export interface ImageVariantElement extends ImageBaseElement {
    kind: ElementKind.image;
}

export interface ImageVariantEmailElement extends ImageBaseElement {
    kind: ElementKind.section;
}

export interface ImageElement {
    kind: ElementKind.image;
    children: (DatasetElement | TextElement)[];
}

export interface ButtonElement {
    kind: ElementKind.button;
    buttonType: ButtonType;
    children: (ButtonActionElement | ButtonTextElement)[];
}

export interface ButtonActionElement {
    kind: ElementKind.buttonAction;
    children: (DatasetElement | TextElement)[];
}

export interface ButtonTextElement {
    kind: ElementKind.buttonText;
    children: (DatasetElement | TextElement)[];
}

export interface DatasetElement {
    kind: ElementKind.dataset;
    id: string;
    datasetId?: number;
    datasetField?: number;
    characterCount?: number;
    variantType?: string;
    children: any[];
}

export interface CMSPlaceholderElement {
    kind: ElementKind.cms_placeholder;
    id: string;
    cmsPlaceholderId?: string;
    children: any[];
}

export interface AttributePlaceholderElement {
    kind: ElementKind.attributePlaceholder;
    id: string;
    defaultValue: string;
    attributeName: string;
    children: TextElement[];
}

export interface LinkElement {
    kind: ElementKind.link;
    id: string;
    children: TextElement[];
}

export interface SurfaceButtonElement {
    kind: ElementKind.surfaceButton;
    surfaceFieldLabel: string;
    surfaceFieldName: string;
    surfaceFieldType: string;
    children: any[];
}

export const DatasetComponentColor = aampeTheme.colors.blueLight500;

export interface ButtonPreviewModel {
    text: string;
    action: string;
}

// export interface MessagePreviewButtonModelBase {
//     formula_id: number;
//     id: string;
//     text: string;
//     action_id?: string;
//     platform: string;
//     uri: string;
//     action: string;
// }
export interface MessagePreviewModelBase {
    formula_id: number;
    id: string;
    image_url?: string;
    formula_type?: FormulaType;
    metadata?: {
        [key in MessageMetadataOptions]?: string | ButtonPreviewModel;
    };
    isSkeleton?: boolean;
    phonePreview?: boolean;
    isSecondaryView?: boolean;
}

export enum MessageMetadataOptions {
    button_primary = 'button_primary',
    button_secondary = 'button_secondary',
}

export interface MobilePushPreviewBase {
    content: {
        [SectionType.header]: string;
        [SectionType.body]: string;
    };
    buttons?: NotificationButtonElement[];
    isSecondaryView?: boolean;
}
export type MobilePushPreviewModel = MessagePreviewModelBase &
    MobilePushPreviewBase;

export interface WebPushPreviewBase {
    content: {
        [SectionType.header]: string;
        [SectionType.body]: string;
    };
}

export type WebPushPreviewModel = MessagePreviewModelBase & WebPushPreviewBase;
export interface WhatsappPreviewBase {
    content: {
        [SectionType.message]: string;
    };
}
export type WhatsappPreviewModel = MessagePreviewModelBase &
    WhatsappPreviewBase;

export interface SMSPreviewBase {
    content: {
        [SectionType.message]: string;
    };
}
export type SMSPreviewModel = MessagePreviewModelBase & SMSPreviewBase;

export interface InAppBannerPreviewBase {
    content: {
        [SectionType.message]: string;
    };
    image_url?: string;
}
export type InAppBannerPreviewModel = MessagePreviewModelBase &
    InAppBannerPreviewBase;

export interface InAppFullPreviewBase {
    content: {
        [SectionType.header]: string;
        [SectionType.body]: string;
    };
    metadata: {
        [MessageMetadataOptions.button_primary]?: ButtonPreviewModel;
        [MessageMetadataOptions.button_secondary]?: ButtonPreviewModel;
        image?: ButtonPreviewModel;
    };
}
export type InAppFullPreviewModel = MessagePreviewModelBase &
    InAppFullPreviewBase;

export interface EmailPreviewBase {
    content: {
        [SectionType.subject]: string;
        [SectionType.emailBody]: string;
    };
}
export interface ContentCardsPreviewBase {
    content: {
        [SectionType.headline]: string;
        [SectionType.body]: string;
        [SectionType.destination_link_on_image_click]: string;
        [SectionType.buttonLink]: string;
        [SectionType.buttonText]: string;
    };
}
export type EmailPreviewModel = MessagePreviewModelBase & EmailPreviewBase;
export type ContentCardsPreviewModel = MessagePreviewModelBase &
    ContentCardsPreviewBase;

export type GenericMessagePreviewModel =
    | MobilePushPreviewModel
    | WebPushPreviewModel
    | WhatsappPreviewModel
    | SMSPreviewModel
    | InAppBannerPreviewModel
    | EmailPreviewModel
    | ContentCardsPreviewModel;

export interface VariantKindSelectedElement {
    elementId: string;
    kind: ElementKind;
    variantType: string;
}

export type FormulaContentPreviewModel = MobilePushPreviewBase &
    WebPushPreviewBase &
    WhatsappPreviewBase &
    SMSPreviewBase &
    InAppBannerPreviewBase &
    InAppFullPreviewBase &
    EmailPreviewBase;
